import React, { useEffect, useState, useRef } from "react";
import { MdCalendarToday } from "react-icons/md";
import { IoIosArrowDown, IoIosArrowUp } from "react-icons/io";
import InputBox from "./InputBox";
import moment from "moment";
import { useTranslation } from "react-i18next";

const DateSelector = ({ handleChange }) => {
  const { t, i18n } = useTranslation();
  const [showDropdown, setShowDropdown] = useState(false);
  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(new Date());
  const [tempStartDate, setTempStartDate] = useState(new Date());
  const [tempEndDate, setTempEndDate] = useState(new Date());
  
  const containerRef = useRef(null);

  // Smart formatter: drops the year if it's the current year to save space!
  const formatDisplayDate = (date) => {
    const m = moment(date);
    const locale = i18n.resolvedLanguage?.startsWith("hi")
      ? "hi-IN"
      : "en-IN";
    return new Intl.DateTimeFormat(locale, {
      day: "numeric",
      month: "short",
      ...(m.year() === moment().year() ? {} : { year: "2-digit" }),
    }).format(m.toDate());
  };

  const selectedRange = `${formatDisplayDate(startDate)} - ${formatDisplayDate(endDate)}`;

  const handleArrowClick = () => setShowDropdown(!showDropdown);

  // Added isInitial flag so it only auto-applies when the component first mounts
  const adjustDateRange = (date, isInitial = false) => {
    const selectedDate = new Date(date);
    const year = selectedDate.getFullYear();
    const month = selectedDate.getMonth();
    const startDay =
      Math.floor((selectedDate.getDate() - 1) / 10) * 10 + 1;
    const lastDay = new Date(year, month + 1, 0).getDate();
    const newStartDate = new Date(year, month, startDay);
    const newEndDate = new Date(
      year,
      month,
      Math.min(startDay + 9, lastDay),
    );

    setTempStartDate(newStartDate);
    setTempEndDate(newEndDate);
    
    // Only apply immediately on the first render, otherwise wait for the Apply button
    if (isInitial) {
      setStartDate(newStartDate);
      setEndDate(newEndDate);
      handleChange(
        moment(newStartDate).format("YYYY-MM-DD"),
        moment(newEndDate).format("YYYY-MM-DD")
      );
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Run the initial setup when the page loads
  useEffect(() => {
    adjustDateRange(new Date(), true);
  }, []); 

  // NOTE: The rogue useEffect that was causing the auto-apply bug has been deleted from here!

  const handleStartDateChange = (e) =>
    setTempStartDate(new Date(e.target.value));
  const handleEndDateChange = (e) => setTempEndDate(new Date(e.target.value));

  const handleApplyClick = () => {
    setStartDate(tempStartDate);
    setEndDate(tempEndDate);
    handleChange(
      moment(tempStartDate).format("YYYY-MM-DD"),
      moment(tempEndDate).format("YYYY-MM-DD")
    );
  };

  const subtractMonths = (date, months) => {
    const result = new Date(date);
    const originalDay = result.getDate();
    result.setDate(1);
    result.setMonth(result.getMonth() - months);
    const targetMonthLastDay = new Date(
      result.getFullYear(),
      result.getMonth() + 1,
      0,
    ).getDate();
    result.setDate(Math.min(originalDay, targetMonthLastDay));
    return result;
  };

  return (
    <div ref={containerRef} className="relative w-full mx-auto">
      {/* Trigger Button */}
      <button
        type="button"
        aria-label={t("dateRange.choose")}
        aria-expanded={showDropdown}
        className={`cursor-pointer w-full border rounded-xl px-3 py-2.5 flex items-center justify-between transition-all duration-300 shadow-sm ${
          showDropdown 
            ? "border-primary ring-1 ring-primary bg-white" 
            : "border-line bg-paper/50 hover:border-primary/50"
        }`}
        onClick={handleArrowClick}
      >
        <div className="flex items-center gap-2 min-w-0">
          <MdCalendarToday className="text-[15px] text-primary flex-shrink-0" />
          <span className="text-[12px] font-bold text-primary truncate">
            {selectedRange}
          </span>
        </div>
        {showDropdown ? (
          <IoIosArrowUp className="text-[16px] text-primary flex-shrink-0 ml-1" />
        ) : (
          <IoIosArrowDown className="text-[16px] text-primary flex-shrink-0 ml-1" />
        )}
      </button>

      {/* Dropdown Panel */}
      {showDropdown && (
        <div className="absolute z-[100] mt-2 w-full left-0 bg-white rounded-xl shadow-2xl border border-line p-4">
          
          <div className="grid grid-cols-2 gap-3">
            <InputBox
              type="date"
              value={moment(tempStartDate).format("YYYY-MM-DD")}
              label={t("dateRange.start")}
              onValueChange={handleStartDateChange}
            />
            <InputBox
              type="date"
              value={moment(tempEndDate).format("YYYY-MM-DD")}
              label={t("dateRange.end")}
              onValueChange={handleEndDateChange}
            />
          </div>

          <div className="grid grid-cols-2 gap-2 mt-4">
            {[
              { label: t("dateRange.last_10_days"), days: 10 },
              { label: t("dateRange.one_month"), months: 1 },
              { label: t("dateRange.three_months"), months: 3 },
              { label: t("dateRange.six_months"), months: 6 },
              { label: t("dateRange.all_time"), all: true },
            ].map((option, idx) => (
              <button
                type="button"
                key={idx}
                onClick={() => {
                  const today = new Date();
                  let newStart = new Date(today);

                  if (option.all) {
                    newStart = new Date("2020-01-01");
                  } else if (option.days) {
                    newStart.setDate(today.getDate() - (option.days - 1));
                  } else {
                    newStart = subtractMonths(today, option.months);
                  }

                  setTempStartDate(newStart);
                  setTempEndDate(today);
                }}
                className={`px-3 py-2 text-[11px] font-bold uppercase tracking-wide border rounded-lg transition-colors ${
                  idx === 4 ? "col-span-2" : "" 
                } bg-paper border-line text-[#6B7280] hover:text-primary hover:border-primary/30`}
              >
                {option.label}
              </button>
            ))}
          </div>

          <button
            type="button"
            onClick={() => {
              handleApplyClick();
              setShowDropdown(false);
            }}
            className="w-full mt-4 py-3 rounded-xl font-slab font-bold text-white text-[14px] tracking-wide bg-primary shadow-sm active:scale-[0.98] transition-all duration-150"
          >
            {t("dateRange.apply")}
          </button>
        </div>
      )}
    </div>
  );
};

export default DateSelector;
